import type { Key } from 'react-aria-components';

/** Maps a list of string values to BUI Select `{ value, label }` options. */
export const toOptions = (values: readonly string[]) =>
  values.map(value => ({ value, label: value }));

/** Reads the single selected value from a BUI Select onChange payload. */
export const selectedKey = (keys: Key | Key[] | null): string | undefined => {
  const key = Array.isArray(keys) ? keys[0] : keys;
  return key === null || key === undefined ? undefined : String(key);
};

/** Reads all selected values from a multi-select onChange payload. */
export const selectedKeys = (keys: Key | Key[] | null): string[] => {
  if (keys === null || keys === undefined) {
    return [];
  }
  return (Array.isArray(keys) ? keys : [keys]).map(String);
};

/**
 * Sorts selected options to the top, keeping each group alphabetical, so a
 * long option list still shows what is currently selected without scrolling.
 */
export const withSelectedFirst = <T extends { value: string; label: string }>(
  options: T[],
  selected: readonly string[],
): T[] => {
  if (selected.length === 0) {
    return options;
  }

  const selectedValues = new Set(selected);
  return [...options].sort((left, right) => {
    const leftSelected = selectedValues.has(left.value);
    const rightSelected = selectedValues.has(right.value);
    if (leftSelected !== rightSelected) {
      return leftSelected ? -1 : 1;
    }
    return left.label.localeCompare(right.label);
  });
};
